/**
 * Сравнение вариантов производственной программы и утверждение бюджета (UC-5).
 *
 * Вайрфрейм (одобрен founder 26.08):
 *   `docs/superpowers/prototypes/season-planning-2027/06-compare-approve.html`
 *
 * Что экран обещает руководителю:
 *   • варианты стоят КОЛОНКАМИ и посчитаны по одним правилам — себестоимость,
 *     выручка, маржа, урожайность, снимок ТМЦ, выполнимость, светофор целей;
 *   • выбранный вариант подсвечен, и видно, какой из них утверждён СЕЙЧАС;
 *   • принятые риски по дефициту техники перечислены явно (Р26) — решение о
 *     бюджете принимается вместе с ними, а не «где-то там было согласовано»;
 *   • кнопка утверждения перечисляет ПОСЛЕДСТВИЯ до нажатия, а не после;
 *   • возврат обязателен с адресатом и словами: «что-то не так» — не задание
 *     на переделку.
 *
 * Чего здесь сознательно нет:
 *   • **своей арифметики.** Каждое число приходит с бэка тем же расчётом, что
 *     кормит сводку одного варианта. Посчитать здесь хотя бы маржу значило бы
 *     завести вторую правду о бюджете;
 *   • **блокировки по выполнимости.** «Не проверялась» — валидное состояние
 *     (Р33): парк может быть не заведён, и это не повод не утверждать бюджет.
 *     Пометка едет вместе с вариантом.
 */
import { useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import { VERDICT_BADGE, VERDICT_TITLES, type TargetMetric } from '@/lib/planning/budgetTargets';
import {
  formatDate,
  formatHa,
  formatMln,
  formatRubPerHa,
  formatTons,
} from '@/lib/planning/planVariant';
import {
  ADDRESSEE_TITLES,
  PLAN_STATUS_TITLES,
  approveConsequences,
  bestVariantIds,
  coverageLine,
  cropShareLine,
  cropYieldLine,
  useApproveProgram,
  useProgramCompare,
  useReturnProgram,
  type CompareVariant,
  type ProgramCompare,
  type ReturnAddressee,
} from '@/lib/planning/programCompare';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { cn } from '@/lib/utils';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';

export function ProgramComparePage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as { farm?: string; variant?: string };
  const navigate = useNavigate();
  const season = useSeasonByYear(year);
  const canRead = useCan('tech_cards', 'r');
  const canDecide = useCan('season_plan', 'u');

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897). Одно
  // хозяйство подставляется само; «первое из списка» молча не берём — это
  // врать про источник.
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/programs/compare',
        params: { year: String(year) },
        search: { farm: undefined, variant: undefined },
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/programs/compare',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;

  const compare = useProgramCompare(canRead ? farmId : null, season.seasonId);
  const data = compare.data;

  const selectedId = search.variant ?? data?.approved.scenario_id ?? data?.variants[0]?.scenario.id ?? null;
  const selected = data?.variants.find((v) => v.scenario.id === selectedId) ?? null;

  const setSearch = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: '/planning/seasons/$year/programs/compare',
      params: { year: String(year) },
      search: { farm: search.farm, variant: selectedId ?? undefined, ...patch },
    });
  };

  if (!season.validYear || season.missing) {
    return (
      <Shell year={params.year}>
        <SeasonYearOutOfWindowCard year={year} />
      </Shell>
    );
  }
  if (season.isLoading) {
    return (
      <Shell year={params.year}>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }
  if (season.notOpened) {
    return (
      <Shell year={params.year}>
        <SeasonNotStartedCard year={year} onOpened={() => undefined} />
      </Shell>
    );
  }
  if (!canRead) {
    return (
      <Shell year={params.year}>
        <Alert>
          <AlertDescription>
            У вас нет доступа к экономике вариантов производственной программы.
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell year={params.year} farmName={data?.farm_name}>
      <FarmScopeNotice scope={farmScope} />
      {data && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Сезон {data.season_year} · вариантов в сравнении: {data.variants.length} ·{' '}
            {PLAN_STATUS_TITLES[data.approved.plan_status] ?? data.approved.plan_status}
            {data.approved.revision_no > 0 ? ` · пересмотр №${data.approved.revision_no}` : ''}
          </span>
        </div>
      )}

      {compare.isLoading && <Skeleton className="h-96 w-full" />}
      {compare.isError && (
        <Alert variant="destructive">
          <AlertDescription>{describeApiError(compare.error)}</AlertDescription>
        </Alert>
      )}

      {data && data.variants.length === 0 && (
        <Alert>
          <AlertDescription>
            У этого хозяйства нет вариантов плана на сезон {year} — сравнивать нечего. Заведите
            вариант посевного плана и назначьте техкарты.
          </AlertDescription>
        </Alert>
      )}

      {data && data.variants.length > 0 && (
        <>
          {!data.prices_visible && (
            <Alert>
              <AlertDescription>
                Деньги реализации скрыты правами: видны структура, покрытие и себестоимость. Выручку,
                маржу и сумму закупок показывает бюджетный справочник — попросите доступ.
              </AlertDescription>
            </Alert>
          )}
          {data.truncated && (
            <Alert>
              <AlertDescription>
                Вариантов больше, чем помещается в одно сравнение — показаны утверждённый, план
                сезона и самые ранние. Лишние архивируйте, чтобы колонок стало меньше.
              </AlertDescription>
            </Alert>
          )}

          <CompareTable
            data={data}
            selectedId={selectedId}
            onSelect={(id) => setSearch({ variant: id })}
          />

          <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
            <RisksCard variant={selected} decisions={data.decisions} />
            <div className="flex flex-col gap-4">
              <ApproveCard variant={selected} data={data} canDecide={canDecide} />
              <ReturnCard variant={selected} canDecide={canDecide} />
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}

// ── Таблица сравнения ────────────────────────────────────────────────────────

function CompareTable({
  data,
  selectedId,
  onSelect,
}: {
  data: ProgramCompare;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const variants = data.variants;
  // «Ниже всех» / «выше всех» — только когда сравнивать есть с чем.
  const bestCost = bestVariantIds(variants, (v) => v.totals.cost, 'lower');
  const bestRevenue = bestVariantIds(variants, (v) => v.totals.revenue, 'higher');
  const bestMargin = bestVariantIds(variants, (v) => v.totals.margin, 'higher');

  const cell = (v: CompareVariant, content: React.ReactNode) => (
    <TableCell key={v.scenario.id} className={cn('align-top', v.scenario.id === selectedId && 'bg-primary/10')}>
      {content}
    </TableCell>
  );

  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-56 align-bottom text-xs uppercase text-muted-foreground">
                Показатель
              </TableHead>
              {variants.map((v) => (
                <TableHead
                  key={v.scenario.id}
                  className={cn('align-bottom', v.scenario.id === selectedId && 'bg-primary/10')}
                >
                  <div className="text-sm font-bold text-foreground">{v.scenario.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {v.is_approved && <Badge variant="primary">рабочий бюджет</Badge>}
                    {v.scenario.is_selected && !v.is_approved && (
                      <Badge variant="secondary">план сезона</Badge>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="mt-2"
                    variant={v.scenario.id === selectedId ? 'default' : 'outline'}
                    onClick={() => onSelect(v.scenario.id)}
                  >
                    {v.scenario.id === selectedId ? '✓ Выбран' : 'Выбрать'}
                  </Button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Посевной план</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <div className="text-sm">{formatHa(v.coverage.total_ha)} под культурами</div>
                    <div className="text-xs text-muted-foreground">{cropShareLine(v.crops)}</div>
                  </>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Себестоимость</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <div className="text-sm font-bold tabular-nums">
                      {formatMln(v.totals.cost)}
                      {bestCost.has(v.scenario.id) && (
                        <Badge variant="primary" className="ml-2">
                          ниже всех
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{formatRubPerHa(v.totals.cost_per_ha)}</div>
                  </>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Выручка</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <div className="text-sm font-bold tabular-nums">
                    {formatMln(v.totals.revenue)}
                    {bestRevenue.has(v.scenario.id) && (
                      <Badge variant="primary" className="ml-2">
                        выше всех
                      </Badge>
                    )}
                  </div>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Маржа</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <div className="text-sm font-bold tabular-nums">
                      {formatMln(v.totals.margin)}
                      {bestMargin.has(v.scenario.id) && (
                        <Badge variant="primary" className="ml-2">
                          выше всех
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatRubPerHa(v.totals.margin_per_ha)}
                    </div>
                  </>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">
                Урожайность (канон — план)
              </TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <div className="text-sm">{cropYieldLine(v.crops)}</div>
                    <div className="text-xs text-muted-foreground">
                      валовой сбор {formatTons(v.totals.gross_t)}
                    </div>
                  </>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">
                Снимок ТМЦ-потребности
              </TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  v.snapshot ? (
                    <>
                      <div className="text-sm">
                        версия {v.snapshot.version_no} · {formatDate(v.snapshot.created_at)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatMln(v.snapshot.total_amount)}
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">не снимался</span>
                  ),
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Выполнимость</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <Badge variant={v.feasibility.status === 'not_checked' ? 'secondary' : 'accent-brand'}>
                      {v.feasibility.label}
                    </Badge>
                    {v.feasibility.note && (
                      <div className="mt-1 text-xs text-muted-foreground">{v.feasibility.note}</div>
                    )}
                  </>,
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">Цели бюджета</TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  v.targets ? (
                    <div className="flex flex-col gap-1">
                      <GoalLine label="Выручка" metric={v.targets.revenue} format={formatMln} />
                      <GoalLine label="Маржа" metric={v.targets.margin} format={formatMln} />
                      <GoalLine
                        label="Маржа, %"
                        metric={v.targets.margin_pct}
                        format={(x) => (x === null ? '—' : `${x.toFixed(1)} %`)}
                      />
                      {!v.targets.comparable && (
                        <span className="text-xs text-muted-foreground">
                          сверка неполная: техкарты назначены не на всю площадь
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">цели не заданы</span>
                  ),
                ),
              )}
            </TableRow>

            <TableRow>
              <TableCell className="text-sm font-medium text-muted-foreground">
                Покрытие полей техкартами
              </TableCell>
              {variants.map((v) =>
                cell(
                  v,
                  <>
                    <div className="text-sm">{coverageLine(v.coverage)}</div>
                    {v.coverage.unassigned_ha > 0 && (
                      <div className="text-xs text-muted-foreground">
                        без техкарты {formatHa(v.coverage.unassigned_ha)}
                      </div>
                    )}
                  </>,
                ),
              )}
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function GoalLine({
  label,
  metric,
  format,
}: {
  label: string;
  metric: TargetMetric;
  format: (value: number | null) => string;
}) {
  return (
    <span className="flex items-center gap-2 text-xs">
      <Badge variant={VERDICT_BADGE[metric.verdict]}>{VERDICT_TITLES[metric.verdict]}</Badge>
      <span className="text-muted-foreground">
        {label}: {format(metric.fact)}
        {metric.target !== null ? ` из ${format(metric.target)}` : ''}
      </span>
    </span>
  );
}

// ── Принятые риски (Р26) ─────────────────────────────────────────────────────

function RisksCard({
  variant,
  decisions,
}: {
  variant: CompareVariant | null;
  decisions: ProgramCompare['decisions'];
}) {
  if (!variant) return null;
  const risks = variant.feasibility.accepted_risks;
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">
          Принятые риски по варианту «{variant.scenario.name}»
        </h3>
        {risks.length === 0 ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {variant.feasibility.status === 'not_checked'
              ? 'Выполнимость по парку техники не проверялась — принятых рисков нет, но и гарантии по срокам тоже. Это не мешает утверждению: пометка едет вместе с вариантом.'
              : 'Узких мест по технике не зафиксировано.'}
          </p>
        ) : (
          <div className="mt-2 flex flex-col divide-y divide-border">
            {risks.map((risk, index) => (
              <div key={`${risk.window_label}-${index}`} className="py-2">
                <div className="text-sm font-semibold text-foreground">
                  {risk.window_label} · {risk.what}
                </div>
                {risk.comment && <div className="text-sm text-muted-foreground">«{risk.comment}»</div>}
                <div className="text-xs text-muted-foreground">
                  {risk.is_accepted
                    ? `${risk.accepted_by ?? 'Плановик'} · ${formatDate(risk.accepted_at)}`
                    : 'Риск не принят — узкое место остаётся открытым'}
                </div>
              </div>
            ))}
          </div>
        )}

        {decisions.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-foreground">История решений</h4>
            <div className="mt-1 flex flex-col divide-y divide-border">
              {decisions.slice(0, 6).map((d) => (
                <div key={d.id} className="py-2 text-sm">
                  <span className="font-medium text-foreground">
                    {d.action === 'approve' ? 'Утверждён' : 'Возврат на доработку'}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    · «{d.scenario_name ?? 'вариант'}» · {formatDate(d.created_at)}
                    {d.addressee ? ` · ${ADDRESSEE_TITLES[d.addressee]}` : ''}
                  </span>
                  {d.comment && <div className="text-muted-foreground">«{d.comment}»</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Утверждение ──────────────────────────────────────────────────────────────

function ApproveCard({
  variant,
  data,
  canDecide,
}: {
  variant: CompareVariant | null;
  data: ProgramCompare;
  canDecide: boolean;
}) {
  const approve = useApproveProgram();
  const [comment, setComment] = useState('');
  if (!variant) return null;

  const consequences = approveConsequences(variant, data.approved.scenario_id !== null);
  const alreadyApproved = variant.is_approved && data.approved.plan_status === 'planned';

  return (
    <Card className="border-primary">
      <CardContent className="p-4">
        <h3 className="text-sm font-bold text-foreground">Утвердить «{variant.scenario.name}»</h3>

        {alreadyApproved ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Этот вариант уже рабочий бюджет сезона (утверждён {formatDate(data.approved.approved_at)}
            {data.approved.snapshot_version_no
              ? `, рабочий снимок ТМЦ версии ${data.approved.snapshot_version_no}`
              : ''}
            ). Чтобы что-то поменять — верните на доработку: правки поедут новой версией.
          </p>
        ) : (
          <ul className="mt-2 list-disc pl-4 text-sm leading-6 text-muted-foreground">
            {consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}

        {!alreadyApproved && variant.approve_blockers.length > 0 && (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription>
              {variant.approve_blockers.join(' · ')}. Производственная программа не финализируется
              без техкарт — утверждение идёт целиком за пару «посевной план × набор техкарт».
            </AlertDescription>
          </Alert>
        )}

        {!alreadyApproved && (
          <>
            <textarea
              // Многострочного примитива в design system пока нет (ui/ даёт
              // только Input) — берём нативный элемент с ТЕМИ ЖЕ токенами,
              // что у Input, а не заводим локальный клон компонента.
              className="mt-3 min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              placeholder="Комментарий утверждающего — необязательно"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              aria-label="Комментарий утверждающего"
            />
            <Button
              className="mt-3 w-full"
              disabled={!canDecide || !variant.can_approve || approve.isPending}
              onClick={() => approve.mutate({ scenarioId: variant.scenario.id, comment })}
            >
              Утвердить как рабочий бюджет
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              {canDecide
                ? 'Утверждает руководитель · фиксируется автор и дата'
                : 'Утверждение доступно тому, кто ведёт план сезона — попросите руководителя'}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Возврат на доработку ─────────────────────────────────────────────────────

function ReturnCard({ variant, canDecide }: { variant: CompareVariant | null; canDecide: boolean }) {
  const back = useReturnProgram();
  const [addressee, setAddressee] = useState<ReturnAddressee>('sowing_plan');
  const [comment, setComment] = useState('');
  if (!variant) return null;

  const ready = comment.trim().length >= 3;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">Вернуть на доработку</h3>
        <div className="mt-2 flex flex-wrap gap-2">
          {(['sowing_plan', 'tech_cards'] as const).map((key) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={addressee === key ? 'default' : 'outline'}
              onClick={() => setAddressee(key)}
            >
              {ADDRESSEE_TITLES[key]}
            </Button>
          ))}
        </div>
        <textarea
          className="mt-2 min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          placeholder="Что именно переделать. Без слов возврат не принимается."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          aria-label="Комментарий возврата"
        />
        <Button
          className="mt-3"
          size="sm"
          variant="destructive"
          disabled={!canDecide || !ready || back.isPending}
          onClick={() =>
            back.mutate(
              { scenarioId: variant.scenario.id, addressee, comment },
              { onSuccess: () => setComment('') },
            )
          }
        >
          Вернуть с комментарием
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          Возврат создаёт новую версию: утверждённое не редактируется, история сравнения остаётся
          целой.
        </p>
      </CardContent>
    </Card>
  );
}

function Shell({
  year,
  farmName,
  children,
}: {
  year?: string;
  farmName?: string;
  children: React.ReactNode;
}) {
  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <PageHeader
        title={
          farmName
            ? `Сравнение вариантов производственной программы · ${farmName}`
            : 'Сравнение вариантов производственной программы'
        }
        description="Варианты колонками по одним правилам: себестоимость, выручка, маржа, урожайность, ТМЦ, выполнимость и цели бюджета. Утверждение делает вариант рабочим бюджетом и производственной программой сезона."
        breadcrumbs={[
          { label: 'Планирование сезона', to: '/planning' },
          {
            label: `Бюджет сезона ${year ?? ''}`.trim(),
            ...(year ? { to: `/planning/seasons/${year}/budget` } : {}),
          },
          { label: 'Сравнение вариантов ПП' },
        ]}
      />
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </AppShell>
  );
}
